import { formatEmailProxyError } from './demOrderNotifications';
import { isValidEmail } from './emailValidation';
import { fetchPricingUpdateRoles } from './pricingUpdateRoles';
import {
  fetchUnifiedPortalUsers,
  unifiedDisplayNameForEmail,
} from './portalUnifiedUsers';
import type { ApplicationSubmission } from '../types/applicationForm';

export type PricingUpdateNotifyResult = {
  ok: boolean;
  sent: { kind: string; to: string[] }[];
  skipped: { kind: string; reason: string }[];
  error?: string;
};

function uniqueValidEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const email of emails) {
    const trimmed = email.trim();
    const key = trimmed.toLowerCase();
    if (!isValidEmail(trimmed) || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function toHtml(body: string): string {
  const escaped = body
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<html><body><div style="font-family:'Segoe UI',Arial,sans-serif;font-size:14px;color:#333;line-height:1.5;">${escaped.replace(/\n/g, '<br>')}</div></body></html>`;
}

function portalBaseUrl(): string {
  const fromEnv = (import.meta.env.VITE_PORTAL_BASE_URL ?? '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return 'https://portal.banteckhan.com.sg';
}

function submissionUrl(id: string): string {
  return `${portalBaseUrl()}/forms/pricing-update/${id}`;
}

async function sendViaPortalProxy(to: string[], subject: string, html: string): Promise<void> {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured in this portal build.');
  }

  const res = await fetch('/order-email-api', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-DEM-Email-Key': proxyKey,
    },
    body: JSON.stringify({ to, subject, html }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(formatEmailProxyError(text, res.status));
  }

  const payload = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (payload.ok === false) {
    throw new Error(payload.error || 'Order email proxy rejected the send request.');
  }
}

async function trySend(
  kind: string,
  recipients: string[],
  subject: string,
  body: string,
  sent: PricingUpdateNotifyResult['sent'],
  skipped: PricingUpdateNotifyResult['skipped'],
): Promise<void> {
  const to = uniqueValidEmails(recipients);
  if (to.length === 0) {
    skipped.push({ kind, reason: 'No valid recipient email' });
    return;
  }
  try {
    await sendViaPortalProxy(to, subject, toHtml(body));
    sent.push({ kind, to });
  } catch (error) {
    skipped.push({
      kind,
      reason: error instanceof Error ? error.message : 'Send failed',
    });
  }
}

function summaryLines(row: ApplicationSubmission): string {
  const company = row.company_name?.trim() || '—';
  const by = row.submitted_by?.trim() || row.submitter_email?.trim() || '—';
  const link = submissionUrl(row.id);
  return [
    `Ref: ${row.application_no}`,
    `Customer: ${company}`,
    `Submitted by: ${by}`,
    '',
    `Open: ${link}`,
  ].join('\n');
}

/** New request submitted → email Authorised + Approver role lists. */
export async function notifyPricingUpdateSubmitted(
  row: ApplicationSubmission,
): Promise<PricingUpdateNotifyResult> {
  const sent: PricingUpdateNotifyResult['sent'] = [];
  const skipped: PricingUpdateNotifyResult['skipped'] = [];

  try {
    const roles = await fetchPricingUpdateRoles();
    const authorised = roles.filter((r) => r.role === 'authorised').map((r) => r.email);
    const approvers = roles.filter((r) => r.role === 'approver').map((r) => r.email);
    const body = [
      'A new Pricing Update (PUF) request was submitted and needs action.',
      '',
      summaryLines(row),
    ].join('\n');
    const subject = `PUF submitted — ${row.application_no}`;

    await trySend('authorised', authorised, subject, body, sent, skipped);
    await trySend('approver', approvers, subject, body, sent, skipped);
  } catch (error) {
    return {
      ok: false,
      sent,
      skipped,
      error: error instanceof Error ? error.message : 'Notification failed.',
    };
  }

  return { ok: sent.length > 0, sent, skipped };
}

/** Submitted for approval → remind Approvers to review. */
export async function notifyPricingUpdatePendingApproval(
  row: ApplicationSubmission,
): Promise<PricingUpdateNotifyResult> {
  const sent: PricingUpdateNotifyResult['sent'] = [];
  const skipped: PricingUpdateNotifyResult['skipped'] = [];

  try {
    const roles = await fetchPricingUpdateRoles();
    const approvers = roles.filter((r) => r.role === 'approver').map((r) => r.email);
    const body = [
      'A Pricing Update (PUF) request is ready for your approval.',
      '',
      summaryLines(row),
    ].join('\n');
    await trySend(
      'approver',
      approvers,
      `PUF pending approval — ${row.application_no}`,
      body,
      sent,
      skipped,
    );
  } catch (error) {
    return {
      ok: false,
      sent,
      skipped,
      error: error instanceof Error ? error.message : 'Notification failed.',
    };
  }

  return { ok: sent.length > 0, sent, skipped };
}

function decisionReasonFromRow(row: ApplicationSubmission): string {
  const fromReject = row.reject_reason?.trim() || '';
  if (fromReject) return fromReject;
  const data =
    row.form_data && typeof row.form_data === 'object'
      ? (row.form_data as { reason_decision?: unknown })
      : null;
  return String(data?.reason_decision ?? '').trim();
}

/** Approved or permanently rejected → email the original submitter. */
export async function notifyPricingUpdateDecision(
  row: ApplicationSubmission,
  decision: 'approved' | 'rejected',
): Promise<PricingUpdateNotifyResult> {
  const sent: PricingUpdateNotifyResult['sent'] = [];
  const skipped: PricingUpdateNotifyResult['skipped'] = [];
  const submitter = row.submitter_email?.trim() || '';
  const label = decision === 'approved' ? 'approved' : 'rejected';
  const reason = decisionReasonFromRow(row);
  const body = [
    `Your Pricing Update (PUF) request was ${label}.`,
    '',
    summaryLines(row),
    reason ? `\nReason of the decision: ${reason}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  await trySend(
    'submitter',
    [submitter],
    `PUF ${label} — ${row.application_no}`,
    body,
    sent,
    skipped,
  );

  return { ok: sent.length > 0, sent, skipped };
}

/** Approver returned to Authorised → email Authorised role list. */
export async function notifyPricingUpdateReturned(
  row: ApplicationSubmission,
): Promise<PricingUpdateNotifyResult> {
  const sent: PricingUpdateNotifyResult['sent'] = [];
  const skipped: PricingUpdateNotifyResult['skipped'] = [];

  try {
    const roles = await fetchPricingUpdateRoles();
    const authorised = roles.filter((r) => r.role === 'authorised').map((r) => r.email);
    const reason = decisionReasonFromRow(row);
    const body = [
      'A Pricing Update (PUF) request was returned by the Approver and needs changes.',
      '',
      summaryLines(row),
      reason ? `\nReason of the decision: ${reason}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    await trySend(
      'authorised',
      authorised,
      `PUF returned — ${row.application_no}`,
      body,
      sent,
      skipped,
    );
  } catch (error) {
    return {
      ok: false,
      sent,
      skipped,
      error: error instanceof Error ? error.message : 'Notification failed.',
    };
  }

  return { ok: sent.length > 0, sent, skipped };
}

async function formatNotifyRecipientLabels(emails: string[]): Promise<string[]> {
  const users = await fetchUnifiedPortalUsers().catch(() => []);
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const email of emails) {
    const trimmed = email.trim();
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) continue;
    seen.add(key);
    labels.push(unifiedDisplayNameForEmail(users, trimmed)?.trim() || trimmed);
  }
  return labels;
}

export async function formatPricingUpdateNotifySummary(
  result: PricingUpdateNotifyResult,
): Promise<string> {
  if (result.sent.length > 0) {
    const labels = await formatNotifyRecipientLabels(result.sent.flatMap((s) => s.to));
    return `Notified ${labels.join(', ')}.`;
  }
  const reasons = result.skipped.map((s) => `${s.kind}: ${s.reason}`).join('; ');
  if (result.error) return `Notification not sent (${result.error}).`;
  if (reasons) {
    return `Notification not sent (${reasons}). Check Authorised / Approver in Forms settings → Pricing Update.`;
  }
  return 'Notification not sent.';
}

/** Fire-and-log helper so UI flows never block on mail failures. */
export function firePricingUpdateNotify(
  label: string,
  run: () => Promise<PricingUpdateNotifyResult>,
  onDone?: (result: PricingUpdateNotifyResult) => void,
): void {
  void run()
    .then((result) => {
      if (!result.ok || (result.sent?.length ?? 0) === 0) {
        console.warn(`PUF ${label} notification:`, result);
      }
      onDone?.(result);
    })
    .catch((err) => {
      console.warn(`PUF ${label} notification failed:`, err);
      onDone?.({
        ok: false,
        sent: [],
        skipped: [],
        error: err instanceof Error ? err.message : 'Notification failed.',
      });
    });
}
